import { useEffect, useRef, useState, useCallback } from 'react';

export interface TerminalLine {
  text: string;
  kind: 'input' | 'output' | 'error';
}

interface TerminalProps {
  lines: TerminalLine[];
  onCommand: (cmd: string) => void;
  prompt?: string;
  /** Rendu bord-à-bord (sans coins arrondis ni bordure) pour le mode examen. */
  flush?: boolean;
  /** Libellé de l'hôte affiché dans l'en-tête (ex. `candidate@node-1:~$`). */
  title?: string;
  /** Complétion Tab : reçoit la saisie courante, renvoie la saisie complétée. */
  onComplete?: (input: string) => string;
  /** Permet au parent de forcer le focus (ex. après fermeture d'un éditeur). */
  focusSignal?: number;
}

// Terminal maison (zéro dépendance) : une zone de sortie défilante + une ligne
// de saisie. Historique via ↑/↓, complétion via Tab, focus auto au clic.
export function Terminal({ lines, onCommand, prompt = '$', flush = false, title = 'student@ckad-cluster: ~', onComplete, focusSignal }: TerminalProps) {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<string[]>([]);
  const [histIndex, setHistIndex] = useState<number>(-1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lines]);

  // Le parent peut redonner le focus au terminal (ex. sortie d'un éditeur vim).
  useEffect(() => {
    if (focusSignal !== undefined) inputRef.current?.focus();
  }, [focusSignal]);

  const submit = useCallback(() => {
    const cmd = input;
    if (cmd.trim()) {
      setHistory((h) => [...h, cmd]);
    }
    setHistIndex(-1);
    setInput('');
    onCommand(cmd);
  }, [input, onCommand]);

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      // Complétion (comme la complétion bash activée à l'examen).
      e.preventDefault();
      if (onComplete) setInput((cur) => onComplete(cur));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (history.length === 0) return;
      const next = histIndex === -1 ? history.length - 1 : Math.max(0, histIndex - 1);
      setHistIndex(next);
      setInput(history[next]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (histIndex === -1) return;
      const next = histIndex + 1;
      if (next >= history.length) {
        setHistIndex(-1);
        setInput('');
      } else {
        setHistIndex(next);
        setInput(history[next]);
      }
    }
  };

  return (
    <div
      className={`flex flex-col h-full bg-[#0b0f19] overflow-hidden font-mono text-[13px] leading-relaxed ${
        flush ? '' : 'rounded-xl border border-line'
      }`}
      onClick={() => inputRef.current?.focus()}
    >
      <div className="flex items-center gap-2 px-4 py-2.5 bg-[#111827] border-b border-line flex-shrink-0">
        <span className="w-3 h-3 rounded-full bg-red-500/80" />
        <span className="w-3 h-3 rounded-full bg-yellow-500/80" />
        <span className="w-3 h-3 rounded-full bg-green-500/80" />
        <span className="ml-2 text-xs text-fg-subtle">{title}</span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-0.5">
        {lines.map((line, i) => (
          <pre
            key={i}
            className={`whitespace-pre-wrap break-words m-0 ${
              line.kind === 'input'
                ? 'text-emerald-300'
                : line.kind === 'error'
                ? 'text-red-400'
                : 'text-slate-200'
            }`}
          >
            {line.kind === 'input' ? `${prompt} ${line.text}` : line.text}
          </pre>
        ))}

        <div className="flex items-center text-emerald-300">
          <span className="mr-2 select-none">{prompt}</span>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="off"
            className="flex-1 bg-transparent outline-none border-none text-emerald-300 caret-emerald-300"
            aria-label="Terminal command input"
          />
        </div>
      </div>
    </div>
  );
}
